import { InputBaseType } from '@/components/ReportForm/InputBase/types';
import { useTranslation } from '@/i18n';
import {
  FormInput,
  FormInputProps,
} from '@moneyforward/ap-frontend-components';
import { memo, useMemo } from 'react';
import { FieldValues } from 'react-hook-form';
import { PAYEE_FORM_MAX_LENGTH } from '../constants';

export type Props<TFieldValues extends FieldValues> = Omit<
  InputBaseType<TFieldValues>,
  'label' | 'tooltip' | 'caption' | 'hidden'
> &
  FormInputProps<TFieldValues> & {
    maxLength?: number;
  };

const InnerApPayeeFormInputContactEmail = <TFieldValues extends FieldValues>({
  control,
  name,
  inputId,
  required = false,
  maxLength = PAYEE_FORM_MAX_LENGTH.CONTACT_EMAIL,
  ...rest
}: Props<TFieldValues>) => {
  const { t } = useTranslation();

  const rules = useMemo(
    () => ({
      required: required ? t('required') : false,
      maxLength: {
        value: maxLength,
        message: t('ap_payee_contact_person_email_max_error', {
          maxLength: maxLength,
        }),
      },
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: t('ap_payee_contact_person_email_format_error'),
      },
    }),
    [required, t, maxLength]
  );

  return (
    <FormInput<TFieldValues>
      control={control}
      name={name}
      inputId={inputId}
      rules={rules}
      {...rest}
    />
  );
};

InnerApPayeeFormInputContactEmail.displayName =
  'InnerApPayeeFormInputContactEmail';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const ApPayeeFormInputContactEmail = memo((props) => (
  <InnerApPayeeFormInputContactEmail {...props} />
)) as typeof InnerApPayeeFormInputContactEmail;
ApPayeeFormInputContactEmail.displayName = 'ApPayeeFormInputContactEmail';
